import React, { useState, useEffect, useMemo } from "react";
import { PatientEntityType } from "@/lib/supabase/supabaseTypes";
import { Info, Search } from "lucide-react";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import {
	useSheetStore,
	SheetContentType,
} from "@/dentlab/src/store/Sheets/sheet.store";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import {
	rankPotentialDuplicates,
	MATCH_LEVELS,
	STRICTNESS_ORDER,
	MatchStrictness,
	PatientMatchReason,
} from "@/dentlab/src/lib/utils/string-matching";
import { Slider } from "@/dentlab/src/components/ui/slider";
import { filterPatientsBySearch } from "@/dentlab/src/lib/utils/patient-search-filter";

interface PatientSelectorProps {
	keeperPatient: PatientEntityType;
	selectedIds: string[];
	onTogglePatient: (candidate: PatientEntityType, isManual?: boolean) => void;
}

// The slider exposes the three tolerance levels from string-matching.ts. Streng is
// exact; Mittel folds umlauts and forgives a couple of typos or a same-sounding
// spelling (Meier/Meyer); Locker allows one more edit per name for heavier typos.
// Every level requires some similarity in both first and last name. Re-ranking on
// change is pure/in-memory — no DB round-trip.
const STRICTNESS_LABEL: Record<MatchStrictness, string> = {
	strict: "Streng",
	medium: "Mittel",
	loose: "Locker",
};

export const PatientSelector: React.FC<PatientSelectorProps> = ({
	keeperPatient,
	selectedIds,
	onTogglePatient,
}) => {
	const [candidates, setCandidates] = useState<PatientEntityType[]>([]);
	const [strictnessIndex, setStrictnessIndex] = useState(1);
	const [isLoading, setIsLoading] = useState(false);
	const [manualQuery, setManualQuery] = useState("");

	const strictness = STRICTNESS_ORDER[strictnessIndex];

	const patientNameFn = usePatientStore((state) => state.patientName);
	const fetchOrgPatientsForDuplicates = usePatientStore(
		(state) => state.fetchOrgPatientsForDuplicates
	);
	const clientsLookup = useCentralStore((state) => state.clientsLookup);

	const { openSheet } = useSheetStore((state) => ({
		openSheet: state.openSheet,
	}));

	// Load the org's patients once; the strictness toggle only re-ranks below.
	useEffect(() => {
		if (!keeperPatient?.id) return;
		let cancelled = false;
		setIsLoading(true);
		fetchOrgPatientsForDuplicates(keeperPatient.id)
			.then((rows) => {
				if (!cancelled) setCandidates(rows);
			})
			.finally(() => {
				if (!cancelled) setIsLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [keeperPatient?.id, fetchOrgPatientsForDuplicates]);

	const duplicatePatients = useMemo(
		() =>
			rankPotentialDuplicates(
				keeperPatient,
				candidates,
				MATCH_LEVELS[strictness]
			),
		[keeperPatient, candidates, strictness]
	);

	const handleOpenPatientDetails = (candidate: PatientEntityType) => {
		openSheet(SheetContentType.PATIENT, { patient: candidate });
	};

	const handleToggle = (candidate: PatientEntityType, isManual = false) => {
		onTogglePatient(candidate, isManual);
	};

	// Free live search across the already-loaded org patients (partial name,
	// code or birth date), independent of the duplicate ranking, so a patient
	// the matcher never surfaces can still be merged in deliberately. While a
	// query is active the search results REPLACE the suggestion list, so they
	// include suggested duplicates too; only genuinely unsuggested picks count
	// as manual selections.
	const isSearching = manualQuery.trim().length >= 2;
	const rankedIds = useMemo(
		() => new Set(duplicatePatients.map((p) => p.id)),
		[duplicatePatients]
	);
	const manualResults = useMemo(
		() =>
			isSearching ? filterPatientsBySearch(candidates, manualQuery) : [],
		[isSearching, manualQuery, candidates]
	);

	const formatPatientName = (patient: PatientEntityType) => {
		return patientNameFn(patient, true) || "Unbekannter Patient";
	};

	const formatBirthDate = (birthDate: string | null) => {
		if (!birthDate) return "";
		return new Date(birthDate).toLocaleDateString("de-DE");
	};

	const clientLabel = (clientId: string | null) =>
		clientId ? getClientDisplayName(clientsLookup[clientId]) : "";

	const getSimilarityLabel = (score: number, isPartial: boolean) => {
		if (isPartial)
			return { text: "Teildaten", color: "bg-amber-50 text-amber-700" };
		if (score >= 0.95)
			return { text: "Exakt", color: "bg-green-100 text-green-800" };
		if (score >= 0.85)
			return {
				text: "Sehr ähnlich",
				color: "bg-green-50 text-green-700",
			};
		if (score >= 0.7)
			return { text: "Ähnlich", color: "bg-yellow-50 text-yellow-700" };
		return { text: "Möglich", color: "bg-gray-100 text-gray-600" };
	};

	// Why the candidate surfaced despite a differing name — the signal the name
	// matcher is blind to. Spells out the answer to "warum wird er nicht
	// vorgeschlagen": an identical code or birth date now does.
	const getReasonLabel = (reason: PatientMatchReason) => {
		if (reason === "code")
			return {
				text: "Gleicher Code",
				color: "bg-blue-100 text-blue-800",
			};
		if (reason === "birthdate")
			return {
				text: "Gleiches Geb.-Datum",
				color: "bg-blue-50 text-blue-700",
			};
		return null;
	};

	return (
		<div className="space-y-3">
			<div className="flex flex-wrap items-start gap-x-8 gap-y-3 pb-3 border-b">
				<div className="w-72 shrink-0 space-y-2">
					<div className="flex items-center justify-between gap-3">
						<span className="text-sm text-gray-500">
							Genauigkeit der Suche
						</span>
						<span className="text-sm font-medium text-gray-700">
							{STRICTNESS_LABEL[strictness]}
						</span>
					</div>
					<Slider
						min={0}
						max={STRICTNESS_ORDER.length - 1}
						step={1}
						value={[strictnessIndex]}
						onValueChange={(value) => setStrictnessIndex(value[0])}
						aria-label="Genauigkeit der Patientensuche"
						data-testid="patient-merge-strictness-slider"
					/>
					<div className="flex justify-between">
						{STRICTNESS_ORDER.map((level, index) => (
							<button
								key={level}
								type="button"
								onClick={() => setStrictnessIndex(index)}
								className={`text-xs transition-colors ${
									index === strictnessIndex
										? "font-medium text-gray-700"
										: "text-gray-400 hover:text-gray-600"
								}`}
								aria-label={`Genauigkeit auf ${STRICTNESS_LABEL[level]} setzen`}
								data-testid={`patient-merge-strictness-${level}`}
							>
								{STRICTNESS_LABEL[level]}
							</button>
						))}
					</div>
				</div>

				<div className="flex-1 min-w-[16rem] space-y-2">
					<span className="text-sm text-gray-500">
						Patient nicht dabei? Beliebigen Patienten suchen und
						trotzdem zusammenlegen:
					</span>
					<div className="relative">
						<Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
						<input
							type="text"
							value={manualQuery}
							onChange={(e) => setManualQuery(e.target.value)}
							placeholder="Name, Code oder Geburtsdatum"
							className="w-full border border-gray-300 rounded pl-8 pr-3 py-1.5 text-sm"
							aria-label="Patient manuell suchen"
							data-testid="patient-merge-manual-search"
						/>
					</div>
				</div>
			</div>

			{isLoading ? (
				<div className="text-center text-gray-500 py-8">
					Suche nach ähnlichen Patienten...
				</div>
			) : isSearching ? null : duplicatePatients.length === 0 ? (
				<div className="text-center text-gray-500 py-8">
					Keine ähnlichen Patienten gefunden
				</div>
			) : (
				<>
					<p className="text-base text-gray-600">
						{duplicatePatients.length}{" "}
						{duplicatePatients.length === 1
							? "ähnlicher Patient"
							: "ähnliche Patienten"}{" "}
						gefunden:
					</p>
					<div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-2">
						{duplicatePatients.map((patient) => {
							const isPartial =
								!keeperPatient.first_name?.trim() ||
								!keeperPatient.last_name?.trim() ||
								!patient.first_name?.trim() ||
								!patient.last_name?.trim();
							const similarity = getSimilarityLabel(
								patient.similarityScore,
								isPartial
							);
							const reason = getReasonLabel(patient.matchReason);
							const isSelected = selectedIds.includes(patient.id);
							return (
								<div
									key={patient.id}
									onClick={() => handleToggle(patient)}
									role="button"
									aria-pressed={isSelected}
									className={`border rounded-lg p-3 cursor-pointer ${
										isSelected
											? "border-green-500 bg-green-50"
											: "border-gray-200 hover:border-gray-300"
									} transition-colors`}
									data-testid="patient-merge-select-candidate"
								>
									<div className="flex items-center justify-between gap-3">
										<div className="flex flex-1 items-start gap-3">
											<input
												type="checkbox"
												checked={isSelected}
												onChange={() =>
													handleToggle(patient)
												}
												onClick={(e) =>
													e.stopPropagation()
												}
												className="mt-1"
												aria-label={`Patient ${formatPatientName(
													patient
												)} zum Zusammenlegen auswählen`}
												data-testid="patient-merge-select-checkbox"
											/>
											<div className="flex-1">
												<div className="flex items-center gap-2">
													<p className="font-medium text-gray-900">
														{formatPatientName(
															patient
														)}
													</p>
													<span
														className={`text-xs px-2 py-0.5 rounded-full ${similarity.color}`}
													>
														{similarity.text}
													</span>
													{reason && (
														<span
															className={`text-xs px-2 py-0.5 rounded-full ${reason.color}`}
														>
															{reason.text}
														</span>
													)}
												</div>
												<div className="flex items-center gap-3 mt-1">
													{patient.code && (
														<span className="text-sm text-gray-600">
															Code: {patient.code}
														</span>
													)}
													{patient.birth_date && (
														<span className="text-sm text-gray-600">
															Geb.:{" "}
															{formatBirthDate(
																patient.birth_date
															)}
														</span>
													)}
													{clientLabel(
														patient.client_id
													) && (
														<span className="text-sm text-gray-600">
															Praxis:{" "}
															{clientLabel(
																patient.client_id
															)}
														</span>
													)}
												</div>
											</div>
										</div>
										<button
											onClick={(e) => {
												e.stopPropagation();
												handleOpenPatientDetails(
													patient
												);
											}}
											className="p-1 text-gray-400 hover:text-gray-600 self-start"
											title="Details anzeigen"
											aria-label="Patientendetails anzeigen"
										>
											<Info className="w-4 h-4" />
										</button>
									</div>
								</div>
							);
						})}
					</div>
				</>
			)}
			{isSearching && (
				<div className="space-y-2">
					<p className="text-base text-gray-600">
						{manualResults.length}{" "}
						{manualResults.length === 1
							? "Patient gefunden"
							: "Patienten gefunden"}
						:
					</p>

					{manualResults.length > 0 ? (
						<div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-2">
							{manualResults.map((patient) => {
								const isSelected = selectedIds.includes(
									patient.id
								);
								const isSuggested = rankedIds.has(patient.id);
								return (
									<div
										key={patient.id}
										onClick={() =>
											handleToggle(patient, !isSuggested)
										}
										role="button"
										aria-pressed={isSelected}
										className={`border rounded-lg p-3 cursor-pointer ${
											isSelected
												? "border-green-500 bg-green-50"
												: "border-gray-200 hover:border-gray-300"
										} transition-colors`}
										data-testid="patient-merge-manual-candidate"
									>
										<div className="flex items-center justify-between gap-3">
											<div className="flex flex-1 items-start gap-3">
												<input
													type="checkbox"
													checked={isSelected}
													onChange={() =>
														handleToggle(
															patient,
															!isSuggested
														)
													}
													onClick={(e) =>
														e.stopPropagation()
													}
													className="mt-1"
													aria-label={`Patient ${formatPatientName(
														patient
													)} zum Zusammenlegen auswählen`}
												/>
												<div className="flex-1">
													<div className="flex items-center gap-2">
														<p className="font-medium text-gray-900">
															{formatPatientName(
																patient
															)}
														</p>
														{isSuggested ? (
															<span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700">
																Duplikat-Vorschlag
															</span>
														) : (
															<span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
																Manuell
															</span>
														)}
													</div>
													<div className="flex items-center gap-3 mt-1">
														{patient.code && (
															<span className="text-sm text-gray-600">
																Code:{" "}
																{patient.code}
															</span>
														)}
														{patient.birth_date && (
															<span className="text-sm text-gray-600">
																Geb.:{" "}
																{formatBirthDate(
																	patient.birth_date
																)}
															</span>
														)}
														{clientLabel(
															patient.client_id
														) && (
															<span className="text-sm text-gray-600">
																Praxis:{" "}
																{clientLabel(
																	patient.client_id
																)}
															</span>
														)}
													</div>
												</div>
											</div>
											<button
												onClick={(e) => {
													e.stopPropagation();
													handleOpenPatientDetails(
														patient
													);
												}}
												className="p-1 text-gray-400 hover:text-gray-600 self-start"
												title="Details anzeigen"
												aria-label="Patientendetails anzeigen"
											>
												<Info className="w-4 h-4" />
											</button>
										</div>
									</div>
								);
							})}
						</div>
					) : (
						<div className="text-sm text-gray-500 py-2">
							Keine Patienten gefunden
						</div>
					)}
				</div>
			)}
		</div>
	);
};
